import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  TrendingUp, 
  Users, 
  Video, 
  Award,
  BarChart3,
  Repeat
} from "lucide-react";

const benefits = [
  {
    icon: TrendingUp,
    title: "Genera Leads Cualificados",
    description: "El videopodcast atrae a tu cliente ideal mostrando tu expertise. Convierte espectadores en oportunidades.",
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    icon: Award,
    title: "Posiciónate como Referente",
    description: "Cada episodio construye tu autoridad en el sector. En 6 meses serás la primera opción.",
    gradient: "from-purple-500 to-pink-500"
  },
  {
    icon: Repeat,
    title: "Multiplica tu Contenido",
    description: "1 grabación = YouTube + Spotify + 10 Reels + Newsletter. Alimenta todas tus plataformas.",
    gradient: "from-pink-500 to-red-500"
  },
  {
    icon: Users,
    title: "Networking Estratégico",
    description: "Invita a clientes potenciales como invitados. El podcast abre puertas imposibles.",
    gradient: "from-cyan-500 to-blue-500"
  },
  {
    icon: BarChart3,
    title: "ROI Medible",
    description: "Trackea desde la visualización hasta la venta con métricas claras.",
    gradient: "from-blue-500 to-purple-500"
  },
  {
    icon: Video,
    title: "Calidad Profesional",
    description: "Sin parecer una videollamada de Zoom. Producción nivel televisión.",
    gradient: "from-purple-500 to-blue-500"
  }
];

export default function Benefits() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="beneficios"
      className="py-20 bg-gradient-to-br from-slate-50 to-blue-50"
      data-testid="benefits-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-blue font-body font-semibold mb-4">
            Beneficios
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            Por qué el videopodcast es el
            <span className="gradient-text"> formato ganador</span>
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Las empresas que dominan su sector ya lo están usando. Descubre por qué funciona.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="p-6 rounded-2xl bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all group"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${benefit.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <benefit.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-galvani-text mb-2">
                {benefit.title}
              </h3>
              <p className="font-body text-sm text-galvani-textLight leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <span className="font-heading font-extrabold text-3xl md:text-4xl text-galvani-blue">82%</span>
            <p className="text-xs text-galvani-textLight mt-2 font-body">de B2B consume podcasts</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <span className="font-heading font-extrabold text-3xl md:text-4xl text-galvani-purple">4.5x</span>
            <p className="text-xs text-galvani-textLight mt-2 font-body">más engagement que blogs</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <span className="font-heading font-extrabold text-3xl md:text-4xl text-galvani-pink">67%</span>
            <p className="text-xs text-galvani-textLight mt-2 font-body">recuerda la marca después</p>
          </div>
          <div className="text-center p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <span className="font-heading font-extrabold text-3xl md:text-4xl text-galvani-cyan">10+</span>
            <p className="text-xs text-galvani-textLight mt-2 font-body">piezas de contenido por episodio</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
